import {
  Button,
  ListView,
  ListItem,
  Select,
  IconButton,
  FormField,
  Icon,
  colors,
  Link,
} from '@dev-spendesk/grapes';
import React, { type Dispatch, useEffect, useState } from 'react';
import './PayableRuleCreate.css';
import { Trans } from 'react-i18next';

import {
  type Field,
  type SubfilterValueWithId,
} from 'src/core/common/components/FilterBuilder';
import { type FilterBuilderAction } from 'src/core/common/components/FilterBuilder/FilterBuilderForm';
import { useTranslation } from 'src/core/common/hooks/useTranslation';
import { LocalStorageKey } from 'src/core/constants/storage';

import { type Condition } from './utils/validateConditions';
import { type Output } from './utils/validateOutputs';
import { type MCRLimit } from '../../hooks/useGetPayableRulesMCRLimit';

type SubfilterConditionsValueWithId = SubfilterValueWithId & {
  valueError?: string;
  operatorError?: string;
};

type Props = {
  fields: Field[];
  conditions: Condition[];
  outputs: Output[];
  onConditionsChange: (updatedConditions: Condition[]) => void;
  renderSubfilterValue(params: {
    subfilterValue: SubfilterConditionsValueWithId;
    updateSubfilterValue(
      newSubfilterValue: SubfilterConditionsValueWithId,
    ): void;
    subfilterOperatorName: string;
    subfilterValueName: string;
  }): React.ReactNode;
  dispatchFilterAction: Dispatch<FilterBuilderAction>;
  conditionFieldName: string;
  conditionOperatorName: string;
  conditionValueName: string;
  mcrLimit: MCRLimit;
};

const getFieldClass = (condition: Condition) => {
  if (
    condition.field === 'grossAmount' &&
    ['isBetween', 'isNotBetween'].includes(condition.operator)
  ) {
    return 'w-[100px] shrink-0';
  }
  return `${condition.field ? 'w-[160px] shrink-0' : 'w-full'}`;
};

export const PayableRuleConditionsEditor = ({
  fields = [],
  conditions = [],
  outputs,
  onConditionsChange,
  dispatchFilterAction,
  renderSubfilterValue,
  conditionFieldName,
  conditionOperatorName,
  conditionValueName,
  mcrLimit,
}: Props) => {
  const { t } = useTranslation('global');
  useEffect(() => {
    if (conditions.length === 0) {
      onConditionsChange([{ field: '', operator: '', value: '' }]); // Initialize with one empty condition
    }
  }, []);

  const [hasBannerClosed, setHasBannerClosed] = useState(
    localStorage.getItem(LocalStorageKey.PayableRulesMCRLimitBannerClosed) ===
      'true',
  );

  const getErrorMessages = () => {
    const errorsList = conditions.filter(
      (condition) =>
        condition.fieldError || condition.operatorError || condition.valueError,
    );
    if (errorsList.length === 1) {
      return t(
        'bookkeep.integrations.settings.payableRules.errors.conditions.general',
      );
    }
    return t('bookkeep.integrations.settings.payableRules.errors.multiError');
  };

  const isConditionComplete = (condition: Condition) =>
    (condition.field &&
      condition.operator &&
      condition.value !== null &&
      (typeof condition.value !== 'object' ||
        Object.values(condition.value).every(Boolean))) ||
    (condition.field && ['isEmpty', 'isNotEmpty'].includes(condition.operator));

  const handleFieldChange = (index: number, value: string) => {
    const updatedConditions = [...conditions];
    updatedConditions[index] = { field: value, operator: '', value: null };
    onConditionsChange(updatedConditions);
  };

  const handleAddCondition = () => {
    onConditionsChange([
      ...conditions,
      { field: '', operator: '', value: null },
    ]);
  };

  const handleDeleteCondition = (index: number) => {
    if (conditions.length === 1) {
      onConditionsChange([{ field: '', operator: '', value: null }]);
      return;
    }
    const updatedConditions = conditions.filter(
      (_, currentIndex) => currentIndex !== index,
    );
    onConditionsChange(updatedConditions);
  };

  const handleCloseBanner = () => {
    setHasBannerClosed(true);
    localStorage.setItem(
      LocalStorageKey.PayableRulesMCRLimitBannerClosed,
      'true',
    );
  };

  return (
    <div className="mb-8 w-full">
      <ListView aria-label="Conditions List" className="conditions-list">
        {conditions.map((condition, index) => {
          const selectedOutputs = outputs.map((output) => output.field);
          const fieldOptions = fields
            .flatMap((field) => field.options ?? [])
            .filter((field) => !selectedOutputs.includes(field.key));

          return (
            // TODO: enforcing react/no-array-index-key will break multiselect logic. We need to fix key resolution upstream
            <ListItem
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              className={`mb-8 w-full rounded-12 bg-background-secondary-default pb-16 pl-16 pr-8 pt-16 ${
                condition.fieldError ||
                condition.operatorError ||
                condition.valueError
                  ? 'error-bg'
                  : ''
              }`}
            >
              <div className="condition-list-item flex w-full items-center gap-8">
                <FormField
                  label="Field"
                  visuallyHideLabel
                  className={getFieldClass(condition)}
                  alertMessage={condition.fieldError}
                >
                  <Select
                    options={fieldOptions.map((option) => ({
                      label: option.label,
                      value: option.key,
                      key: option.key,
                    }))}
                    value={
                      fieldOptions.find(
                        (option) => option.key === condition.field,
                      ) || undefined
                    }
                    onSelect={(selected) =>
                      handleFieldChange(
                        index,
                        (selected as unknown as { value: string }).value,
                      )
                    }
                    dropdownContentMaxHeight="450px"
                    placeholder={t(
                      'bookkeep.integrations.settings.payableRules.conditions.placeholder',
                    )}
                    className="w-full"
                    name={`${conditionFieldName}-${condition.field}`}
                  />
                </FormField>
                {condition.field && (
                  <>
                    {renderSubfilterValue({
                      subfilterValue: {
                        id: `${index}`,
                        field: condition.field,
                        operator: condition.operator,
                        value: condition.value,
                        valueError: condition.valueError,
                        operatorError: condition.operatorError,
                      },
                      updateSubfilterValue: (newSubfilterValue) => {
                        const updatedConditions = [...conditions];
                        updatedConditions[index] = {
                          // TODO: fix and use propper types
                          field: newSubfilterValue.field ?? '',
                          operator: newSubfilterValue.operator ?? '',
                          value: newSubfilterValue.value ?? null,
                        };
                        onConditionsChange(updatedConditions);
                        dispatchFilterAction({
                          type: 'updateSubfilterValue',
                          subfilterGroupId: `group-${index}`,
                          // TODO: better types
                          subfilterOperator:
                            (newSubfilterValue.operator as 'and' | 'or') ||
                            'and',
                          subfilterValue: newSubfilterValue,
                        });
                      },
                      subfilterOperatorName: `${conditionOperatorName}-${condition.field}`,
                      subfilterValueName: `${conditionValueName}-${condition.field}`,
                    })}
                  </>
                )}
                <IconButton
                  className="my-auto mr-0 shrink-0"
                  hasNegativeMargins
                  name="trash"
                  iconName="trash"
                  variant="tertiaryNeutral"
                  aria-label={`Delete condition ${index}`}
                  onClick={() => handleDeleteCondition(index)}
                />
              </div>
            </ListItem>
          );
        })}
      </ListView>
      {conditions.some(
        (condition) =>
          condition.fieldError ||
          condition.operatorError ||
          condition.valueError,
      ) && (
        <div className="error-messages text-danger-default mb-8 mt-4">
          <output className="_grapes-lb" data-severity="alert">
            <Icon size="m" name="hexagone-cross" />
            <span>{getErrorMessages()}</span>
          </output>
        </div>
      )}

      {(!mcrLimit.limited || mcrLimit.alreadyUsed < mcrLimit.limit) &&
        conditions.some(isConditionComplete) && (
          <Button
            variant="secondaryNeutral"
            onClick={handleAddCondition}
            iconName="plus"
            text={t(
              'bookkeep.integrations.settings.payableRules.conditions.add',
            )}
            className="mb-8 w-full"
          />
        )}

      {mcrLimit.limited && mcrLimit.limit > 0 && (
        <div className="flex items-center gap-8">
          <Icon
            size="m"
            name="circle-information"
            color={colors.contentInfoDefault}
          />
          <span className="text-content-info-default body-m">
            <Trans
              i18nKey={
                mcrLimit.alreadyUsed === mcrLimit.limit
                  ? 'bookkeep.integrations.settings.payableRules.mcrLimits.fullyReachedMessage'
                  : 'bookkeep.integrations.settings.payableRules.mcrLimits.availableLimitMessage'
              }
              values={{
                available: mcrLimit.limit - mcrLimit.alreadyUsed,
                limit: mcrLimit.limit,
              }}
              components={[
                <Link
                  key="intercom-message"
                  onClick={(event) => {
                    event.preventDefault();
                    window.Intercom('show');
                  }}
                  href=""
                >
                  -
                </Link>,
              ]}
            />
          </span>
        </div>
      )}
      {!hasBannerClosed && mcrLimit.limited && mcrLimit.limit === 0 && (
        <div className="start flex place-items-center gap-24 rounded-12 bg-background-secondary-brand-default p-24">
          <div className="flex flex-grow flex-col items-start gap-8">
            <div className="flex w-full items-center justify-between gap-8">
              <h1 className="title-m">
                {t(
                  'bookkeep.integrations.settings.payableRules.mcrLimits.bannerTitle',
                )}
              </h1>
              <IconButton
                key="close"
                variant="tertiaryNeutral"
                aria-label={t('misc.close')}
                onClick={handleCloseBanner}
                iconName="cross"
                className="h-20 w-20"
              />
            </div>
            <p className="text-content-secondary-bg-secondary body-m">
              {t(
                'bookkeep.integrations.settings.payableRules.mcrLimits.bannerContent',
              )}
            </p>
            <Button
              variant="secondaryNeutral"
              text={t(
                'bookkeep.integrations.settings.payableRules.mcrLimits.bannerActionText',
              )}
              onClick={() => window.Intercom('show')}
            />
          </div>
        </div>
      )}
    </div>
  );
};
